import { Button, CategoryIcon, SeverityBadge, WorkspaceTag } from '@aigovops/design-system';
import type { AlertListItem, AlertSeverity } from '../../lib/api';

/**
 * Cross-workspace dedup rollup (PR-P3-4, UC-4 R2 B4 / nav-ia-v2 §9.6).
 *
 * One regulatory signal usually lands in every client workspace a
 * consultant holds. Rendering it as three separate rows makes Nadia
 * read the same EU AI Act headline three times and decide three times;
 * the rollup states it once, names which clients it hit and how many
 * register items each has, and offers the one action that covers all
 * of them.
 *
 * Deliberately NOT a replacement for the rows underneath. The rollup is
 * a lead-in, not a filter: collapsing the members out of the list would
 * mean a signal Nadia dismissed at the rollup level vanishes from the
 * per-client sweep she does afterwards, and the two views would disagree
 * about what is in her inbox.
 */

export interface RollupGroup {
  /** `source_kind::source_id` — the dedup key the backend already uses. */
  key: string;
  title: string;
  category: AlertListItem['category'];
  /** Highest severity across members; a signal is as urgent as its worst tenant. */
  severity: AlertSeverity;
  members: AlertListItem[];
}

const SEVERITY_ORDER: Record<AlertSeverity, number> = {
  critical: 0,
  high: 1,
  elevated: 2,
  moderate: 3,
  info: 4,
};

export function refCount(raw: string): number {
  try {
    const v = JSON.parse(raw) as unknown;
    return Array.isArray(v) ? v.length : 0;
  } catch {
    return 0;
  }
}

/**
 * Groups rows that share a source across ≥2 distinct workspaces.
 *
 * Single-workspace groups are excluded on purpose — a rollup card that
 * says "Affects 1 workspace" is a second copy of the row directly below
 * it, which is noise on the surface whose entire job is reducing a
 * 37-row sweep to a decision.
 */
export function buildRollups(rows: AlertListItem[]): RollupGroup[] {
  const byKey = new Map<string, AlertListItem[]>();
  for (const r of rows) {
    if (!r.source_kind || !r.source_id) continue;
    const key = `${r.source_kind}::${r.source_id}`;
    const bucket = byKey.get(key);
    if (bucket) bucket.push(r);
    else byKey.set(key, [r]);
  }

  const groups: RollupGroup[] = [];
  for (const [key, members] of byKey) {
    const workspaces = new Set(members.map((m) => m.workspace_id));
    if (workspaces.size < 2) continue;
    const worst = members.reduce((acc, m) =>
      SEVERITY_ORDER[m.severity] < SEVERITY_ORDER[acc.severity] ? m : acc
    );
    groups.push({
      key,
      title: worst.title,
      category: worst.category,
      severity: worst.severity,
      members,
    });
  }
  return groups.sort(
    (a, b) =>
      SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
      b.members.length - a.members.length
  );
}

export function AggregateRollup({
  group,
  canConvert,
  canConvertMember,
  busy,
  onConvertAll,
  onFocusGroup,
}: {
  group: RollupGroup;
  canConvert: boolean;
  /**
   * Convert rights for one member, evaluated against ITS OWN client's
   * workspace. Passed in rather than assumed because a consultant's
   * role differs per client, and "Convert on all 3" must never mean
   * "attempt 3, silently land 2".
   */
  canConvertMember: (member: AlertListItem) => boolean;
  busy: boolean;
  onConvertAll: (alertIds: string[]) => void;
  onFocusGroup: () => void;
}) {
  const openMembers = group.members.filter((m) => m.status === 'open');
  const convertible = openMembers.filter((m) => canConvertMember(m));
  const blockedByRole = openMembers.length - convertible.length;
  const workspaceCount = new Set(group.members.map((m) => m.workspace_id)).size;
  const headingId = `rollup-${group.key.replace(/[^a-zA-Z0-9]/g, '-')}`;

  return (
    <section
      aria-labelledby={headingId}
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-elevated)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
        <CategoryIcon category={group.category} />
        <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 'var(--font-size-3xs)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--text-muted)',
              }}
            >
              Affects {workspaceCount} workspaces
            </span>
            <SeverityBadge severity={group.severity === 'info' ? 'standard' : group.severity} />
          </div>
          <h2 id={headingId} style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
            {group.title}
          </h2>
          {/* Named clients + their exposure. This is the whole reason
              the card exists — "3 workspaces" without saying WHICH
              three, and how much each has riding on it, is a number a
              consultant still has to go and look up. */}
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-3)',
            }}
          >
            {group.members.map((m) => {
              const n = refCount(m.register_item_refs);
              return (
                <li
                  key={m.alert_id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <WorkspaceTag name={m.workspace_name ?? 'Unknown workspace'} slug={m.workspace_slug} />
                  <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
                    {n} item{n === 1 ? '' : 's'}
                    {m.status !== 'open' ? ` · ${m.status}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <Button variant="secondary" onClick={onFocusGroup} disabled={busy}>
          Select these {convertible.length}
        </Button>
        {canConvert && convertible.length > 0 ? (
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => onConvertAll(convertible.map((m) => m.alert_id))}
          >
            Convert {convertible.length} →
          </Button>
        ) : null}
      </div>
      {/* Says why the count on the button is smaller than the count in
          the heading, rather than letting the two numbers silently
          disagree — and distinguishes "already done" from "you can't". */}
      {canConvert && convertible.length < group.members.length ? (
        <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          {group.members.length - openMembers.length > 0
            ? `${group.members.length - openMembers.length} already actioned. `
            : ''}
          {blockedByRole > 0
            ? `${blockedByRole} not converted here — you have read-only access in ${
                openMembers
                  .filter((m) => !canConvertMember(m))
                  .map((m) => m.workspace_name ?? 'that workspace')
                  .join(', ')
              }.`
            : ''}
        </span>
      ) : null}
    </section>
  );
}
